import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Progress,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from "antd";
import {
  DeleteOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { PAYROLL_SCHOOL_SECTION_FILTER_OPTIONS } from "../../../utils/payrollSchoolSections";
import Utilities from "../../../dependencies/custom/react-utilities";
import domainSettings from "../../../dependencies/custom/settings";
import { payrollAmountInputProps } from "../../../components/finance/payroll/payrollAmountInput";
import { useNavigate } from "react-router-dom";
import { usePayrollSecurity } from "../../../hooks/finance/usePayrollSecurity";
import PayrollOperationsPanel from "../../../components/finance/payroll/PayrollOperationsPanel";

const { Text, Title } = Typography;
const asList = (data) => (Array.isArray(data) ? data : data?.results || []);
const money = (value) =>
  `UGX ${Number(value || 0).toLocaleString("en-UG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
const amountColumn = (title, dataIndex) => ({
  title,
  dataIndex,
  align: "right",
  render: money,
});
const employeeColumns = [
  { title: "Staff ID", dataIndex: "staffCode" },
  { title: "Employee", dataIndex: "employeeName" },
  { title: "Department", dataIndex: "department" },
];
const newIdempotencyKey = () =>
  window.crypto?.randomUUID?.() ||
  `${Date.now()}-${Math.random().toString(16).slice(2)}`;
const responseFilename = (response, fallback) => {
  const disposition = response.headers.get("content-disposition") || "";
  return disposition.match(/filename="?([^";]+)"?/i)?.[1] || fallback;
};

const PayrollReports = () => {
  const navigate = useNavigate();
  const { payrollAccess } = usePayrollSecurity();
  const currentYear = dayjs().year();
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(null);
  const [departmentId, setDepartmentId] = useState(null);
  const [schoolSection, setSchoolSection] = useState(null);
  const [costCentre, setCostCentre] = useState(null);
  const [payrollStatus, setPayrollStatus] = useState(null);
  const [employeeId, setEmployeeId] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [report, setReport] = useState(null);
  const [accountingReport, setAccountingReport] = useState(null);
  const [approvalHistory, setApprovalHistory] = useState([]);
  const [statutoryRemittance, setStatutoryRemittance] = useState(null);
  const [statutoryRemittanceForm] = Form.useForm();
  const [reportJobs, setReportJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeReport, setActiveReport] = useState("payeSchedule");

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ year: String(year) });
      if (month) params.set("month", String(month));
      if (departmentId) params.set("department_id", String(departmentId));
      if (schoolSection) params.set("school_section", schoolSection);
      if (costCentre) params.set("cost_centre", costCentre);
      if (payrollStatus) params.set("status", payrollStatus);
      if (employeeId) params.set("employee_id", String(employeeId));
      const [response, accountingResponse, approvalResponse] =
        await Promise.all([
          Utilities.fetchWithAuth(
            "GET",
            `${domainSettings.urls.finance.payrollManagementReports}?${params}`,
            null,
            navigate,
          ),
          payrollAccess.accounting
            ? Utilities.fetchWithAuth(
                "GET",
                `${domainSettings.urls.finance.payrollAccountingReports}?${params}`,
                null,
                navigate,
              )
            : Promise.resolve({ ok: true, data: {} }),
          Utilities.fetchWithAuth(
            "GET",
            `${domainSettings.urls.finance.payrollApprovalHistory}?${params}`,
            null,
            navigate,
          ),
        ]);
      if (response.ok) setReport(response.data);
      else Utilities.showError(response, "Could not load payroll reports");
      if (accountingResponse.ok) setAccountingReport(accountingResponse.data);
      if (approvalResponse.ok)
        setApprovalHistory(asList(approvalResponse.data));
    } finally {
      setLoading(false);
    }
  }, [
    costCentre,
    departmentId,
    employeeId,
    month,
    navigate,
    payrollAccess.accounting,
    payrollStatus,
    schoolSection,
    year,
  ]);

  useEffect(() => {
    const fetchDepartments = async () => {
      const [response, employeeResponse] = await Promise.all([
        Utilities.fetchWithAuth(
          "GET",
          domainSettings.urls.finance.departments,
          null,
          navigate,
        ),
        Utilities.fetchWithAuth(
          "GET",
          domainSettings.urls.finance.payrollProfiles,
          null,
          navigate,
        ),
      ]);
      if (response.ok) setDepartments(asList(response.data));
      if (employeeResponse.ok) setEmployees(asList(employeeResponse.data));
    };
    fetchDepartments();
  }, [navigate]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const tables = useMemo(
    () => ({
      payeSchedule: {
        label: "PAYE / URA",
        data: report?.payeSchedule || [],
        columns: [
          { title: "Period", dataIndex: "period" },
          ...employeeColumns,
          { title: "TIN", dataIndex: "tin" },
          amountColumn("Gross pay", "grossPay"),
          amountColumn("Taxable earnings", "taxableEarnings"),
          amountColumn("PAYE chargeable income", "chargeableIncome"),
          amountColumn("PAYE", "paye"),
        ],
      },
      nssfSchedule: {
        label: "NSSF schedule",
        data: report?.nssfSchedule || [],
        columns: [
          { title: "Period", dataIndex: "period" },
          ...employeeColumns,
          { title: "NSSF number", dataIndex: "nssfNumber" },
          amountColumn("Gross pay", "grossPay"),
          amountColumn("Employee NSSF", "employeeNssf"),
          amountColumn("Employer NSSF", "employerNssf"),
          amountColumn("Total NSSF", "totalNssf"),
        ],
      },
      lstSchedule: {
        label: "LST schedule",
        data: report?.lstSchedule || [],
        columns: [
          { title: "Period", dataIndex: "period" },
          ...employeeColumns,
          { title: "Local authority", dataIndex: "localAuthority" },
          amountColumn("Annual liability", "annualLiability"),
          amountColumn("Paid before", "paidBefore"),
          amountColumn("This installment", "installment"),
          amountColumn("Remaining", "remaining"),
          {
            title: "Exempt",
            dataIndex: "exempt",
            render: (value) => (value ? <Tag color="blue">Yes</Tag> : "No"),
          },
          { title: "Reason", dataIndex: "exemptionReason" },
        ],
      },
      lstAnnualHistory: {
        label: "Annual LST history",
        data: report?.lstAnnualHistory || [],
        columns: [
          { title: "Staff ID", dataIndex: "staffCode" },
          { title: "Employee", dataIndex: "employeeName" },
          { title: "Local authority", dataIndex: "location" },
          amountColumn("Annual liability", "annualLiability"),
          amountColumn("Paid", "paid"),
          amountColumn("Remaining", "remaining"),
          { title: "Installments paid", dataIndex: "installmentsPaid" },
          { title: "Exemption reason", dataIndex: "exemptionReason" },
        ],
      },
      departmentSummary: {
        label: "Departments",
        data: report?.departmentSummary || [],
        columns: [
          { title: "Department", dataIndex: "department" },
          { title: "Employees", dataIndex: "employeeCount", align: "center" },
          amountColumn("Basic salary", "basicSalary"),
          amountColumn("Gross pay", "grossPay"),
          amountColumn("PAYE", "paye"),
          amountColumn("Employee NSSF", "employeeNssf"),
          amountColumn("LST", "lst"),
          amountColumn("Other deductions", "otherDeductions"),
          amountColumn("Net pay", "netPay"),
          amountColumn("Employer cost", "employerCost"),
        ],
      },
      sectionSummary: {
        label: "School sections",
        data: report?.sectionSummary || [],
        columns: [
          { title: "School section", dataIndex: "schoolSection" },
          { title: "Employees", dataIndex: "employeeCount" },
          amountColumn("Gross pay", "grossPay"),
          amountColumn("PAYE", "paye"),
          amountColumn("Employee NSSF", "employeeNssf"),
          amountColumn("Net pay", "netPay"),
          amountColumn("Employer cost", "employerCost"),
        ],
      },
      costCentreSummary: {
        label: "Cost centres",
        data: report?.costCentreSummary || [],
        columns: [
          { title: "Cost centre", dataIndex: "costCentre" },
          { title: "Employees", dataIndex: "employeeCount" },
          amountColumn("Gross pay", "grossPay"),
          amountColumn("PAYE", "paye"),
          amountColumn("Employee NSSF", "employeeNssf"),
          amountColumn("Net pay", "netPay"),
          amountColumn("Employer cost", "employerCost"),
        ],
      },
      earningsDeductionsSummary: {
        label: "Earnings & deductions",
        data: report?.earningsDeductionsSummary || [],
        columns: [
          { title: "Code", dataIndex: "componentCode" },
          { title: "Component", dataIndex: "componentName" },
          {
            title: "Type",
            dataIndex: "kind",
            render: (value) => (
              <Tag
                color={
                  value === "Earning"
                    ? "green"
                    : value === "Employee Deduction"
                      ? "red"
                      : "blue"
                }
              >
                {value}
              </Tag>
            ),
          },
          amountColumn("Total amount", "amount"),
        ],
      },
      employerCostReport: {
        label: "Employer cost",
        data: report?.employerCostReport || [],
        columns: [
          { title: "Period", dataIndex: "period" },
          ...employeeColumns,
          amountColumn("Gross pay", "grossPay"),
          amountColumn("Employer NSSF", "employerNssf"),
          amountColumn("Other contributions", "otherEmployerContributions"),
          amountColumn("Employer cost", "employerCost"),
        ],
      },
      bankPaymentReport: {
        label: "Bank payments",
        data: report?.bankPaymentReport || [],
        columns: [
          { title: "Period", dataIndex: "period" },
          { title: "Staff ID", dataIndex: "staffCode" },
          { title: "Employee", dataIndex: "employeeName" },
          { title: "Bank", dataIndex: "bankName" },
          { title: "Account", dataIndex: "bankAccount" },
          amountColumn("Net salary", "amount"),
          {
            title: "Status",
            dataIndex: "status",
            render: (value) => (
              <Tag color={value === "Paid" ? "green" : "orange"}>{value}</Tag>
            ),
          },
          { title: "Reference", dataIndex: "paymentReference" },
          { title: "Payment date", dataIndex: "paymentDate" },
        ],
      },
      salaryHistory: {
        label: "Salary history",
        data: report?.salaryHistory || [],
        columns: [
          ...employeeColumns,
          { title: "From", dataIndex: "effectiveFrom" },
          {
            title: "To",
            dataIndex: "effectiveTo",
            render: (value) => value || "Current",
          },
          { title: "Structure", dataIndex: "salaryStructure" },
          { title: "Scale step", dataIndex: "salaryScaleStep" },
          amountColumn("Basic salary", "basicSalary"),
          { title: "Revision reason", dataIndex: "revisionReason" },
        ],
      },
      yearToDateTotals: {
        label: "Year-to-date",
        data: report?.yearToDateTotals || [],
        columns: [
          ...employeeColumns,
          amountColumn("Gross pay", "grossPay"),
          amountColumn("PAYE", "paye"),
          amountColumn("Employee NSSF", "employeeNssf"),
          amountColumn("Employer NSSF", "employerNssf"),
          amountColumn("LST", "lst"),
          amountColumn("Other deductions", "otherDeductions"),
          amountColumn("Net pay", "netPay"),
          amountColumn("Employer cost", "employerCost"),
        ],
      },
      varianceReport: {
        label: "Employee & component variance",
        data: report?.varianceReport || [],
        columns: [
          { title: "Period", dataIndex: "period" },
          { title: "Previous", dataIndex: "previousPeriod" },
          ...employeeColumns,
          { title: "Code", dataIndex: "componentCode" },
          { title: "Component", dataIndex: "componentName" },
          amountColumn("Previous amount", "previousAmount"),
          amountColumn("Current amount", "currentAmount"),
          amountColumn("Difference", "difference"),
          {
            title: "Change",
            dataIndex: "differencePercent",
            render: (value) =>
              value == null ? "New" : `${Number(value).toFixed(2)}%`,
          },
        ],
      },
      exceptionsReport: {
        label: "Exceptions & warnings",
        data: report?.exceptionsReport || [],
        columns: [
          { title: "Period", dataIndex: "period" },
          {
            title: "Level",
            dataIndex: "severity",
            render: (value) => (
              <Tag
                color={
                  value === "Blocking"
                    ? "red"
                    : value === "Warning"
                      ? "gold"
                      : "blue"
                }
              >
                {value}
              </Tag>
            ),
          },
          { title: "Employee", dataIndex: "employeeName" },
          { title: "Check", dataIndex: "title" },
          { title: "Details", dataIndex: "detail" },
          { title: "Status", dataIndex: "status" },
          {
            title: "Acknowledgement reason",
            dataIndex: "acknowledgementReason",
          },
          { title: "Acknowledged by", dataIndex: "acknowledgedBy" },
        ],
      },
      statutoryExceptions: {
        label: "Statutory exceptions",
        data: report?.statutoryExceptions || [],
        columns: [
          { title: "Period", dataIndex: "period" },
          {
            title: "Area",
            dataIndex: "area",
            render: (value) => <Tag color="red">{value}</Tag>,
          },
          { title: "Staff ID", dataIndex: "staffCode" },
          { title: "Employee", dataIndex: "employeeName" },
          { title: "Problem to correct", dataIndex: "issue" },
        ],
      },
      statutoryRemittances: {
        label: "Statutory remittances",
        data: report?.statutoryRemittances || [],
        columns: [
          { title: "Period", dataIndex: "period" },
          { title: "Type", dataIndex: "type" },
          amountColumn("Expected", "expectedAmount"),
          amountColumn("Remitted", "remittedAmount"),
          amountColumn("Difference", "difference"),
          {
            title: "Status",
            dataIndex: "status",
            render: (value) => (
              <Tag
                color={
                  value === "Remitted"
                    ? "green"
                    : value === "Partially Remitted"
                      ? "orange"
                      : "gold"
                }
              >
                {value}
              </Tag>
            ),
          },
          { title: "Reference", dataIndex: "remittanceReference" },
          { title: "Date", dataIndex: "remittanceDate" },
          {
            title: "",
            render: (_, row) => (
              <Button
                size="small"
                onClick={() => {
                  setStatutoryRemittance(row);
                  statutoryRemittanceForm.setFieldsValue({
                    remittedAmount: Number(row.remittedAmount),
                    remittanceReference: row.remittanceReference,
                    remittanceDate: row.remittanceDate
                      ? dayjs(row.remittanceDate)
                      : dayjs(),
                    notes: row.notes,
                  });
                }}
              >
                Record remittance
              </Button>
            ),
          },
        ],
      },
      advanceLoanReport: {
        label: "Advances & loans",
        data: report?.advanceLoanReport || [],
        columns: [
          { title: "Reference", dataIndex: "reference" },
          ...employeeColumns,
          { title: "Type", dataIndex: "advanceType" },
          { title: "Status", dataIndex: "status" },
          amountColumn("Principal", "principalAmount"),
          amountColumn("Recovered", "recoveredAmount"),
          amountColumn("Outstanding", "outstandingBalance"),
          { title: "Age days", dataIndex: "ageDays" },
          { title: "Next installment", dataIndex: "nextInstallmentDate" },
          amountColumn("Next amount", "nextInstallmentAmount"),
        ],
      },
      budgetComparison: {
        label: "Payroll cost vs budget",
        data: report?.budgetComparison || [],
        columns: [
          { title: "Department", dataIndex: "department" },
          amountColumn("Payroll budget", "payrollBudget"),
          amountColumn("Actual employer cost", "actualEmployerCost"),
          amountColumn("Remaining / over", "variance"),
          {
            title: "Used",
            dataIndex: "utilizationPercent",
            render: (value) => `${Number(value || 0).toFixed(2)}%`,
          },
        ],
      },
      paymentStatusReport: {
        label: "Payment status",
        data: report?.paymentStatusReport || [],
        columns: [
          { title: "Period", dataIndex: "period" },
          { title: "Payroll status", dataIndex: "payrollStatus" },
          { title: "Employees", dataIndex: "employeeCount" },
          { title: "Paid", dataIndex: "paidEmployees" },
          { title: "Unpaid", dataIndex: "unpaidEmployees" },
          amountColumn("Paid amount", "paidAmount"),
          amountColumn("Unpaid amount", "unpaidAmount"),
          amountColumn("Net pay", "totalNetPay"),
        ],
      },
    }),
    [report, statutoryRemittanceForm],
  );

  const fetchReportJobs = useCallback(async () => {
    const response = await Utilities.fetchWithAuth(
      "GET",
      domainSettings.urls.finance.payrollReportJobs,
      null,
      navigate,
    );
    if (response.ok) setReportJobs(asList(response.data));
  }, [navigate]);

  useEffect(() => {
    fetchReportJobs();
  }, [fetchReportJobs]);
  useEffect(() => {
    if (!reportJobs.some((job) => ["Queued", "Running"].includes(job.status)))
      return undefined;
    const timer = window.setInterval(fetchReportJobs, 3000);
    return () => window.clearInterval(timer);
  }, [fetchReportJobs, reportJobs]);

  const queueReport = async (exportFormat) => {
    const reportType =
      activeReport === "history" ? "payrollHistory" : activeReport;
    if (reportType === "accounting") return exportAccounting(exportFormat);
    const response = await Utilities.fetchWithAuth(
      "POST",
      domainSettings.urls.finance.payrollReportJobs,
      {
        reportType,
        exportFormat,
        year,
        month,
        departmentId,
        schoolSection,
        costCentre,
        status: payrollStatus,
        employeeId,
        idempotencyKey: newIdempotencyKey(),
      },
      navigate,
    );
    if (!response.ok)
      return Utilities.showError(
        response,
        "Could not queue the payroll report",
      );
    message.success("Report queued. Its progress appears below.");
    fetchReportJobs();
  };

  const downloadJob = async (job) => {
    const endpoint =
      domainSettings.urls.finance.payrollReportJobDownload.replace(
        ":id",
        job.jobId,
      );
    const response = await fetch(endpoint, {
      headers: { Authorization: `Bearer ${Utilities.getCookie("access")}` },
    });
    if (!response.ok) return message.error("The report file is not ready");
    const url = URL.createObjectURL(await response.blob());
    const link = document.createElement("a");
    link.href = url;
    link.download =
      job.fileName ||
      responseFilename(response, `${job.reportType}.${job.exportFormat}`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const clearFinishedReportJobs = async () => {
    const hasFinishedJobs = reportJobs.some((job) =>
      ["Completed", "Partially Completed", "Failed"].includes(job.status),
    );
    if (!hasFinishedJobs)
      return message.info("There are no finished report jobs to clear");
    const response = await Utilities.fetchWithAuth(
      "POST",
      domainSettings.urls.finance.payrollReportJobsClear,
      { allFinished: true },
      navigate,
    );
    if (!response.ok)
      return Utilities.showError(response, "Could not clear report jobs");
    message.success(`${response.data.cleared} finished report job(s) cleared`);
    fetchReportJobs();
  };

  const retryReportJob = async (job) => {
    const endpoint = domainSettings.urls.finance.payrollReportJobRetry.replace(
      ":id",
      job.jobId,
    );
    const response = await Utilities.fetchWithAuth(
      "POST",
      endpoint,
      {},
      navigate,
    );
    if (!response.ok)
      return Utilities.showError(response, "Could not retry the report job");
    message.success("Report retry queued safely");
    fetchReportJobs();
  };

  const exportAccounting = async (format) => {
    const params = new URLSearchParams({ year: String(year), export: format });
    if (month) params.set("month", String(month));
    if (departmentId) params.set("department_id", String(departmentId));
    const response = await fetch(
      `${domainSettings.urls.finance.payrollAccountingReports}?${params}`,
      { headers: { Authorization: `Bearer ${Utilities.getCookie("access")}` } },
    );
    if (!response.ok) {
      message.error("Could not export the accounting report");
      return;
    }
    const url = URL.createObjectURL(await response.blob());
    const link = document.createElement("a");
    link.href = url;
    link.download = responseFilename(
      response,
      `Payroll_Accounting_${year}.${format}`,
    );
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const recordStatutoryRemittance = async () => {
    const values = await statutoryRemittanceForm.validateFields();
    const url =
      domainSettings.urls.finance.payrollStatutoryRemittanceRecord.replace(
        ":id",
        statutoryRemittance.remittanceId,
      );
    const response = await Utilities.fetchWithAuth(
      "POST",
      url,
      {
        ...values,
        remittanceDate: values.remittanceDate.format("YYYY-MM-DD"),
      },
      navigate,
    );
    if (!response.ok)
      return Utilities.showError(
        response,
        "Could not record the statutory remittance",
      );
    message.success("Statutory remittance recorded");
    setStatutoryRemittance(null);
    fetchReports();
  };

  const historyTab = (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Row gutter={[12, 12]}>
        <Col xs={12} md={6}>
          <Statistic
            title="Completed runs"
            value={report?.yearlyHistory?.payrollRunCount || 0}
          />
        </Col>
        <Col xs={12} md={6}>
          <Statistic
            title="Employee payments"
            value={report?.yearlyHistory?.employeePayments || 0}
          />
        </Col>
        <Col xs={12} md={6}>
          <Statistic
            title="Year gross pay"
            value={money(report?.yearlyHistory?.grossPay)}
          />
        </Col>
        <Col xs={12} md={6}>
          <Statistic
            title="Year employer cost"
            value={money(report?.yearlyHistory?.employerCost)}
          />
        </Col>
      </Row>
      <Card title="Monthly history" size="small">
        <Table
          rowKey="month"
          dataSource={report?.monthlyHistory || []}
          pagination={false}
          columns={[
            { title: "Month", dataIndex: "month" },
            { title: "Employee payments", dataIndex: "employeeCount" },
            amountColumn("Gross pay", "grossPay"),
            amountColumn("Net pay", "netPay"),
            amountColumn("Employer cost", "employerCost"),
          ]}
        />
      </Card>
      <Card title="Payroll run history" size="small">
        <Table
          rowKey="runId"
          dataSource={report?.payrollHistory || []}
          pagination={false}
          scroll={{ x: 950 }}
          columns={[
            { title: "Period", dataIndex: "period" },
            {
              title: "Type",
              dataIndex: "payrollType",
              render: (value) => <Tag>{value}</Tag>,
            },
            {
              title: "Status",
              dataIndex: "status",
              render: (value) => (
                <Tag
                  color={
                    value === "Paid"
                      ? "green"
                      : value === "Reversed"
                        ? "red"
                        : "blue"
                  }
                >
                  {value}
                </Tag>
              ),
            },
            { title: "Employees", dataIndex: "employeeCount" },
            amountColumn("Gross pay", "grossPay"),
            amountColumn("Net pay", "netPay"),
            amountColumn("Employer cost", "employerCost"),
            { title: "Reversal reason", dataIndex: "reversalReason" },
          ]}
        />
      </Card>
    </Space>
  );

  const tabItems = [
    ...Object.entries(tables)
      .slice(0, 6)
      .map(([key, table]) => ({
        key,
        label: table.label,
        children: (
          <Table
            rowKey={(_, index) => index}
            dataSource={table.data}
            columns={table.columns}
            loading={loading}
            scroll={{ x: 1000 }}
          />
        ),
      })),
    { key: "history", label: "Monthly & yearly history", children: historyTab },
    ...Object.entries(tables)
      .slice(6)
      .map(([key, table]) => ({
        key,
        label: table.label,
        children: (
          <Table
            rowKey={(_, index) => index}
            dataSource={table.data}
            columns={table.columns}
            loading={loading}
            scroll={{ x: 1000 }}
          />
        ),
      })),
    {
      key: "accounting",
      label: "Accounting & reconciliation",
      children: (
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Alert
            type="info"
            showIcon
            message="Posted salary creates a salary amount owed. Confirmed bank payments reduce that amount through payment-clearing journals."
          />
          <Row gutter={[12, 12]}>
            <Col xs={24} md={8}>
              <Card>
                <Statistic
                  title="Salary posted"
                  value={money(accountingReport?.salaryPayable?.posted)}
                />
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card>
                <Statistic
                  title="Payments cleared"
                  value={money(accountingReport?.salaryPayable?.cleared)}
                />
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card>
                <Statistic
                  title="Still payable"
                  value={money(accountingReport?.salaryPayable?.outstanding)}
                />
              </Card>
            </Col>
          </Row>
          <Card title="Statutory and other liabilities" size="small">
            <Table
              rowKey="type"
              pagination={false}
              dataSource={accountingReport?.statutoryAndOtherLiabilities || []}
              columns={[
                { title: "Liability", dataIndex: "type" },
                amountColumn("Credits", "credit"),
                amountColumn("Debits / settlements", "debit"),
                amountColumn("Outstanding", "outstanding"),
              ]}
            />
          </Card>
          <Row gutter={[12, 12]}>
            <Col xs={24} lg={8}>
              <Card title="Payroll expense by department" size="small">
                <Table
                  rowKey="department"
                  pagination={false}
                  dataSource={accountingReport?.expenseByDepartment || []}
                  columns={[
                    { title: "Department", dataIndex: "department" },
                    amountColumn("Expense", "amount"),
                  ]}
                />
              </Card>
            </Col>
            <Col xs={24} lg={8}>
              <Card title="Payroll expense by cost centre" size="small">
                <Table
                  rowKey="costCentre"
                  pagination={false}
                  dataSource={accountingReport?.expenseByCostCentre || []}
                  columns={[
                    { title: "Cost centre", dataIndex: "costCentre" },
                    amountColumn("Expense", "amount"),
                  ]}
                />
              </Card>
            </Col>
            <Col xs={24} lg={8}>
              <Card title="Advance receivable reductions" size="small">
                <Table
                  rowKey="department"
                  pagination={false}
                  dataSource={
                    accountingReport?.advanceRecoveriesByDepartment || []
                  }
                  columns={[
                    { title: "Department", dataIndex: "department" },
                    amountColumn("Recovered", "amount"),
                  ]}
                />
              </Card>
            </Col>
          </Row>
          <Card title="Posted journal history" size="small">
            <Table
              rowKey="journalId"
              dataSource={accountingReport?.journalHistory || []}
              columns={[
                { title: "Journal", dataIndex: "journalNumber" },
                {
                  title: "Type",
                  dataIndex: "journalType",
                  render: (v) => <Tag>{v}</Tag>,
                },
                {
                  title: "Status",
                  dataIndex: "status",
                  render: (v) => (
                    <Tag color={v === "Posted" ? "green" : "red"}>{v}</Tag>
                  ),
                },
                amountColumn("Debit", "totalDebit"),
                amountColumn("Credit", "totalCredit"),
                { title: "Posted by", dataIndex: "postedByName" },
                {
                  title: "Posted at",
                  dataIndex: "postedAt",
                  render: (v) => dayjs(v).format("DD MMM YYYY HH:mm"),
                },
              ]}
              scroll={{ x: 900 }}
            />
          </Card>
        </Space>
      ),
    },
    {
      key: "approvalHistory",
      label: "Approval history",
      children: (
        <Table
          rowKey="runStepId"
          dataSource={approvalHistory}
          loading={loading}
          scroll={{ x: 1000 }}
          columns={[
            { title: "Payroll", dataIndex: "periodName" },
            { title: "Cycle", dataIndex: "cycle" },
            { title: "Step", dataIndex: "name" },
            { title: "Type", dataIndex: "stepType" },
            {
              title: "Decision",
              dataIndex: "status",
              render: (v) => (
                <Tag
                  color={
                    v === "Approved"
                      ? "green"
                      : v === "Rejected"
                        ? "red"
                        : "blue"
                  }
                >
                  {v}
                </Tag>
              ),
            },
            { title: "Decided by", dataIndex: "decidedByName" },
            { title: "Delegated from", dataIndex: "delegatedFromName" },
            {
              title: "Time",
              dataIndex: "decidedAt",
              render: (v) => (v ? dayjs(v).format("DD MMM YYYY HH:mm") : "—"),
            },
            { title: "Comment", dataIndex: "decisionComment" },
          ]}
        />
      ),
    },
  ].filter((item) => {
    if (["payeSchedule", "lstSchedule", "lstAnnualHistory"].includes(item.key))
      return payrollAccess.tax;
    if (item.key === "nssfSchedule") return payrollAccess.nssf;
    if (["statutoryExceptions", "statutoryRemittances"].includes(item.key))
      return payrollAccess.tax || payrollAccess.nssf;
    if (item.key === "advanceLoanReport") return payrollAccess.advance;
    if (["accounting", "budgetComparison"].includes(item.key))
      return payrollAccess.accounting;
    if (item.key === "bankPaymentReport")
      return payrollAccess.bank && payrollAccess.salary_full;
    if (
      [
        "salaryHistory",
        "yearToDateTotals",
        "employerCostReport",
        "varianceReport",
        "exceptionsReport",
      ].includes(item.key)
    )
      return payrollAccess.salary_full;
    return payrollAccess.salary_full || payrollAccess.salary_summary;
  });

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <div>
        <Title level={3} style={{ marginBottom: 4 }}>
          Payroll reports
        </Title>
        <Text type="secondary">
          Statutory schedules, management analysis, payroll history and
          year-to-date totals.
        </Text>
      </div>
      <PayrollOperationsPanel />
      <Card size="small">
        <Space wrap>
          <Select
            value={year}
            onChange={setYear}
            style={{ width: 120 }}
            options={Array.from({ length: 8 }, (_, index) => ({
              value: currentYear - index,
              label: currentYear - index,
            }))}
          />
          <Select
            allowClear
            placeholder="All months"
            value={month}
            onChange={setMonth}
            style={{ width: 160 }}
            options={Array.from({ length: 12 }, (_, index) => ({
              value: index + 1,
              label: dayjs().month(index).format("MMMM"),
            }))}
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="All departments"
            value={departmentId}
            onChange={setDepartmentId}
            style={{ width: 220 }}
            options={departments.map((department) => ({
              value: department.departmentId,
              label: department.name,
            }))}
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="All school sections"
            value={schoolSection}
            onChange={(value) => setSchoolSection(value || null)}
            options={PAYROLL_SCHOOL_SECTION_FILTER_OPTIONS}
            style={{ width: 180 }}
          />
          <Input
            allowClear
            placeholder="Cost centre"
            value={costCentre}
            onChange={(event) => setCostCentre(event.target.value || null)}
            style={{ width: 160 }}
          />
          <Select
            allowClear
            placeholder="Payroll status"
            value={payrollStatus}
            onChange={setPayrollStatus}
            style={{ width: 170 }}
            options={["Finalized", "Partially Paid", "Paid"].map((value) => ({
              value,
              label: value,
            }))}
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Employee"
            value={employeeId}
            onChange={setEmployeeId}
            style={{ width: 220 }}
            options={employees.map((employee) => ({
              value: employee.profileId,
              label: `${employee.staffName || employee.staffEmail} · ${employee.staffCode || "No ID"}`,
            }))}
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchReports}
            loading={loading}
          >
            Refresh
          </Button>
          <Button
            icon={<FileExcelOutlined />}
            onClick={() => queueReport("xlsx")}
            disabled={loading}
          >
            Excel
          </Button>
          <Button
            icon={<FileTextOutlined />}
            onClick={() => queueReport("csv")}
            disabled={loading}
          >
            CSV
          </Button>
          <Button
            icon={<FilePdfOutlined />}
            onClick={() => queueReport("pdf")}
            disabled={loading}
          >
            PDF
          </Button>
        </Space>
      </Card>
      <Row gutter={[16, 16]}>
        <Col xs={12} lg={4}>
          <Card>
            <Statistic
              title="Employees"
              value={report?.overview?.employeeCount || 0}
            />
          </Card>
        </Col>
        <Col xs={12} lg={4}>
          <Card>
            <Statistic
              title="Gross pay"
              value={money(report?.overview?.grossPay)}
            />
          </Card>
        </Col>
        <Col xs={12} lg={4}>
          <Card>
            <Statistic title="PAYE" value={money(report?.overview?.paye)} />
          </Card>
        </Col>
        <Col xs={12} lg={4}>
          <Card>
            <Statistic
              title="Total NSSF"
              value={money(
                Number(report?.overview?.employeeNssf || 0) +
                  Number(report?.overview?.employerNssf || 0),
              )}
            />
          </Card>
        </Col>
        <Col xs={12} lg={4}>
          <Card>
            <Statistic title="LST" value={money(report?.overview?.lst)} />
          </Card>
        </Col>
        <Col xs={12} lg={4}>
          <Card>
            <Statistic
              title="Net pay"
              value={money(report?.overview?.netPay)}
            />
          </Card>
        </Col>
      </Row>
      <Card>
        <Tabs
          activeKey={activeReport}
          onChange={setActiveReport}
          items={tabItems}
        />
      </Card>
      {reportJobs.length > 0 && (
        <Card
          title="Generated report jobs"
          size="small"
          extra={
            <Popconfirm
              title="Clear all finished report jobs?"
              description="Generated files will be removed. Running and queued jobs will remain."
              onConfirm={clearFinishedReportJobs}
            >
              <Button danger size="small" icon={<DeleteOutlined />}>
                Clear finished
              </Button>
            </Popconfirm>
          }
        >
          <Table
            rowKey="jobId"
            dataSource={reportJobs.slice(0, 10)}
            pagination={false}
            columns={[
              {
                title: "File name",
                render: (_, row) => row.fileName || row.reportType,
              },
              {
                title: "Format",
                dataIndex: "exportFormat",
                render: (value) => <Tag>{value.toUpperCase()}</Tag>,
              },
              {
                title: "Status",
                dataIndex: "status",
                render: (value) => (
                  <Tag
                    color={
                      value === "Completed"
                        ? "green"
                        : value === "Failed"
                          ? "red"
                          : value === "Partially Completed"
                            ? "orange"
                            : "blue"
                    }
                  >
                    {value}
                  </Tag>
                ),
              },
              {
                title: "Progress",
                render: (_, row) => (
                  <Progress
                    percent={row.progress}
                    size="small"
                    status={row.status === "Failed" ? "exception" : undefined}
                  />
                ),
              },
              { title: "Rows", dataIndex: "rowCount" },
              { title: "Attempts", dataIndex: "attemptCount" },
              {
                title: "",
                render: (_, row) => (
                  <Space>
                    {row.hasFile && (
                      <Button
                        size="small"
                        icon={<DownloadOutlined />}
                        onClick={() => downloadJob(row)}
                      >
                        Download
                      </Button>
                    )}
                    {["Failed", "Partially Completed"].includes(row.status) && (
                      <Button
                        size="small"
                        icon={<ReloadOutlined />}
                        onClick={() => retryReportJob(row)}
                      >
                        Retry
                      </Button>
                    )}
                  </Space>
                ),
              },
            ]}
          />
        </Card>
      )}
      <Modal
        title="Record statutory remittance"
        open={Boolean(statutoryRemittance)}
        onCancel={() => setStatutoryRemittance(null)}
        onOk={recordStatutoryRemittance}
      >
        {statutoryRemittance && (
          <Alert
            showIcon
            type="info"
            message={`${statutoryRemittance.type} · ${statutoryRemittance.period}`}
            description={`Payroll liability: ${money(statutoryRemittance.expectedAmount)}. Record the amount actually sent and its official reference.`}
            style={{ marginBottom: 16 }}
          />
        )}
        <Form form={statutoryRemittanceForm} layout="vertical">
          <Form.Item
            name="remittedAmount"
            label="Amount sent"
            rules={[{ required: true }]}
          >
            <InputNumber
              {...payrollAmountInputProps}
              min={0}
              max={Number(statutoryRemittance?.expectedAmount || 0)}
              style={{ width: "100%" }}
            />
          </Form.Item>
          <Form.Item
            name="remittanceReference"
            label="Payment/remittance reference"
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="remittanceDate"
            label="Date sent"
            rules={[{ required: true }]}
          >
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};

export default PayrollReports;
